import React, { type ReactElement, type ReactNode, type KeyboardEvent } from 'react';

import classnames from 'classnames';

import { Icon, Input, type InputSize, Label, Popover, Select } from 'styled-system';

import styles from './Search.module.scss';

type SearchProps = {
    className?: string;
    popupClassName?: string;
    placeholder: string;
    search: string;
    onChangeSearch: (value: string) => void;
    characterHint?: string;
    isShowCharacterHint?: boolean;
    prefix?: ReactNode;
    maxLength?: number;
    autoFocus?: boolean;
    size?: InputSize;
    onBlur?: () => void;
};

const SearchIcon = () => <Icon name="s-icon-magnifying-glass" className={styles.searchIcon} />;

export const Search = ({
    className,
    popupClassName,
    placeholder,
    search,
    onChangeSearch,
    characterHint,
    isShowCharacterHint,
    prefix = <SearchIcon />,
    maxLength,
    size = 'default',
    ...rest
}: SearchProps): ReactElement => {
    const getPopoverContent = () => {
        if (isShowCharacterHint) {
            return (
                <div className={styles.searchCharacterHintOption}>
                    <Icon name="s-icon-comment-simple" className={styles.searchCharacterHintIcon} />
                    <Label>{characterHint}</Label>
                </div>
            );
        }
        return null;
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onChangeSearch(e.currentTarget.value);
        }
    };

    const handleChange = (value: string) => {
        onChangeSearch(value);
    };

    return (
        <Popover
            content={getPopoverContent()}
            placement="bottom"
            noArrow
            align={{ offset: [4, 0] }}
            className={popupClassName}
            destroyOnHide
        >
            <Select
                autoComplete
                className={classnames(styles.search, className)}
                onChange={handleChange}
                options={[]}
                disableVirtualScroll={isShowCharacterHint}
                value={search}
                {...rest}
            >
                <Input
                    autoComplete="true"
                    maxLength={maxLength}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    prefix={prefix}
                    size={size}
                />
            </Select>
        </Popover>
    );
};

Search.displayName = 'Search';
